import React from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface MetricCoverageCalendarProps {
  fromDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  missingDates: string[]; // Array of YYYY-MM-DD strings
}

interface CalendarMonth {
  year: number;
  month: number; // 0-11
  weeks: (string | null)[][]; // Grid of dates or null (for padding)
}

const MetricCoverageCalendar: React.FC<MetricCoverageCalendarProps> = ({
  fromDate,
  endDate,
  missingDates,
}) => {
  const missingSet = new Set(missingDates);

  // Round dates to month boundaries
  const startDate = new Date(`${fromDate}T00:00:00.000Z`);
  const roundedStart = new Date(
    Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1),
  );

  const endDateObj = new Date(`${endDate}T00:00:00.000Z`);
  const roundedEnd = new Date(
    Date.UTC(endDateObj.getUTCFullYear(), endDateObj.getUTCMonth() + 1, 0),
  );

  const [currentMonth, setCurrentMonth] = React.useState<CalendarMonth>(
    generateMonthCalendar(
      roundedStart.getUTCFullYear(),
      roundedStart.getUTCMonth(),
    ),
  );

  function generateMonthCalendar(year: number, month: number): CalendarMonth {
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const startingDayOfWeek = firstDay.getUTCDay();
    const daysInMonth = lastDay.getUTCDate();

    const weeks: (string | null)[][] = [];
    let currentWeek: (string | null)[] = new Array(startingDayOfWeek).fill(
      null,
    );

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      currentWeek.push(dateStr);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return { year, month, weeks };
  }

  const handlePrevMonth = () => {
    const prevMonth = currentMonth.month === 0 ? 11 : currentMonth.month - 1;
    const prevYear =
      currentMonth.month === 0 ? currentMonth.year - 1 : currentMonth.year;
    const newCalendar = generateMonthCalendar(prevYear, prevMonth);

    const newDate = new Date(Date.UTC(prevYear, prevMonth, 1));
    if (newDate >= roundedStart) {
      setCurrentMonth(newCalendar);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = currentMonth.month === 11 ? 0 : currentMonth.month + 1;
    const nextYear =
      currentMonth.month === 11 ? currentMonth.year + 1 : currentMonth.year;
    const newCalendar = generateMonthCalendar(nextYear, nextMonth);

    const newDate = new Date(Date.UTC(nextYear, nextMonth + 1, 0));
    if (newDate <= roundedEnd) {
      setCurrentMonth(newCalendar);
    }
  };

  const monthName = new Date(
    Date.UTC(currentMonth.year, currentMonth.month, 1),
  ).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const canGoPrev =
    new Date(Date.UTC(currentMonth.year, currentMonth.month, 1)) > roundedStart;
  const canGoNext =
    new Date(Date.UTC(currentMonth.year, currentMonth.month + 1, 0)) <
    roundedEnd;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Coverage Calendar
        </Typography>

        {/* Month Navigation */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            size="small"
            startIcon={<ChevronLeftIcon />}
            onClick={handlePrevMonth}
            disabled={!canGoPrev}
          >
            Previous
          </Button>

          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, minWidth: 150, textAlign: "center" }}
          >
            {monthName}
          </Typography>

          <Button
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={handleNextMonth}
            disabled={!canGoNext}
          >
            Next
          </Button>
        </Stack>

        {/* Calendar Table */}
        <Box sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: 300,
              "& th, & td": {
                padding: "4px",
                textAlign: "center",
                fontSize: "0.75rem",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                {dayNames.map((day) => (
                  <TableCell key={day} sx={{ fontWeight: 700 }}>
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentMonth.weeks.map((week, weekIdx) => (
                <TableRow key={weekIdx}>
                  {week.map((dateStr, dayIdx) => {
                    const isWithinRange =
                      dateStr && dateStr >= fromDate && dateStr <= endDate;
                    const isMissing = dateStr && missingSet.has(dateStr);
                    const isOutOfRange = dateStr && !isWithinRange;

                    let bgColor = "#ffffff";
                    let textColor = "#000000";

                    if (isOutOfRange) {
                      bgColor = "#f0f0f0";
                      textColor = "#cccccc";
                    } else if (isWithinRange && isMissing) {
                      bgColor = "#ffcccc"; // Light red for missing
                      textColor = "#cc0000";
                    } else if (isWithinRange && !isMissing) {
                      bgColor = "#ccffcc"; // Light green for has data
                      textColor = "#008000";
                    }

                    return (
                      <TableCell
                        key={dayIdx}
                        sx={{
                          backgroundColor: bgColor,
                          color: textColor,
                          border: "1px solid #e0e0e0",
                          fontWeight: dateStr ? 500 : 400,
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                      >
                        {dateStr ? dateStr.slice(8) : ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Legend */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: "#ccffcc",
                border: "1px solid #008000",
              }}
            />
            <Typography variant="caption">Has Data</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: "#ffcccc",
                border: "1px solid #cc0000",
              }}
            />
            <Typography variant="caption">Missing Data</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: "#f0f0f0",
                border: "1px solid #cccccc",
              }}
            />
            <Typography variant="caption">Out of Range</Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MetricCoverageCalendar;
